/* eslint-disable class-methods-use-this */
import { History } from 'history';
import { Dispatch } from 'redux';
import * as Yup from 'yup';
import { GpsType } from '../../../../../../global/components/layout/layout-models';
import { ProgressItem } from '../../../../../../global/components/navigation/generic-stepper';
import { formatDate, formatDateMonthNumber } from '../../../../../../global/helpers/form-data-helper';
import { getCurrencyFormat } from '../../../../../../global/helpers/format-helpers';
import { YesNoIndicators } from '../../../../../../global/models/api-model';
import { Institution } from '../../../../../../global/models/institution-model';
import { Person } from '../../../../../../global/models/person-model';
import { TcsmEpiApiState } from '../../../../../../root/tcsm-model';
import { FormTypes } from '../../../../../account-info/models/form-models';
import { ArcRoutes } from '../../../../constants/routes';
import { DocuSignRequest } from '../../../docusign/models/docusign-models';
import { PlanSummary } from '../../institution-model';
import { PensionPlanDetail } from '../../plan-history/plan-history-models';
import { AdoptionMemberType, AdoptionStep, ContributionAmountType, ContributionType, EmployerContributionStatusType, IContributionOverride, LayDcPlanAdoptionContentModel, LayDcPlanFormModel, ParticipantModel, PlanAdoptionFormTypes, PlanCancelRequest, PlanFormModel, PlanSubmitRequest, QccoStatus } from '../models/plan-adoption-form-models';

import { PlanAdoptionRoutes } from '../routes';
import { PlanAdoptionWizard } from './plan-adoption-wizard';
import { calculateQccoStatus, createQccoValidationSchema } from '../shared/qcco-questions-shared';

export interface QccoPlanAdoptionOptions {
	formType: PlanAdoptionFormTypes,
	dispatch?: Dispatch<any>,
	history?: History<unknown>,
	epiContent?: TcsmEpiApiState,
	featureRootPath?: string,
	institutionRootPath?: string,
	location?: any,
	dcPlanDetail?: PensionPlanDetail,
	rsvpPlanDetail?: PensionPlanDetail,
	person: Person,
	currentInstitution: Institution,
	planSummary: PlanSummary,
	authToken?: string,
	loginTime?: string,
}

export abstract class QccoPlanAdoptionWizard extends PlanAdoptionWizard {


	readonly wizardOptions: QccoPlanAdoptionOptions;

	constructor(options: QccoPlanAdoptionOptions) {
		super(options?.dispatch, options?.history, options?.epiContent);
		this.wizardOptions = options;
	}

	getFormContentModel(): LayDcPlanAdoptionContentModel {

		const institutionSettings = this.epiContent?.institutionSettingsState?.epiContent;

		return {
			pageTitle: '',
			PageDescription: '',
			planAdoptionDetailTitle: institutionSettings?.adoptLayDCDetailsTitle,
			adoptingInstitutionLabel: institutionSettings?.adoptLayDCadoptionInstitutionTitle,
			clientIdLabel: institutionSettings?.clientIdLabel,
			adoptionDateLabel: institutionSettings?.planAdoptionDateLabel,
			signHereLabel: institutionSettings?.adoptLayDBSignLabel,
			signHereTooltip: institutionSettings?.adoptLayDBSignTooltip,
			signHereDescription: institutionSettings?.adoptLayDBSignHelper,
			gpsType: GpsType.LayDbAdoption,
			cancelLabel: 'Cancel',
			submitLabel: 'Save',
			qccoQuestionTitle: institutionSettings?.qccoLayRsvpTitle,
			qccoQuestion1Label: institutionSettings?.qccoQuestion1Label,
			qccoQuestion2Label: institutionSettings?.qccoQuestion2Label,
			qccoQuestion3Label: institutionSettings?.qccoQuestion3Label,
			qccoQualificationStatusTitle: institutionSettings?.qccoQualificationStatusTitle,
			qccoQualificationStatusText: institutionSettings?.qccoQualificationStatusText,
			qccoQualificationYesStatusTitle: institutionSettings?.qccoQualificationYesStatusTitle,
			qccoQualificationYesStatusText: institutionSettings?.qccoQualificationYesStatusText,
			qccoQualificationNoStatusTitle: institutionSettings?.qccoQualificationNoStatusTitle,
			qccoQualificationNoStatusText: institutionSettings?.qccoQualificationNoStatusText,
			qccoQualificationErrorStatusText: institutionSettings?.qccoQualificationErrorStatusText,
			qccoQualificationErrorStatusTitle: institutionSettings?.qccoQualificationErrorStatusTitle,
			adoptEmployerBaseContributionLabel: institutionSettings?.adoptLayDCEmployerBaseContributionLabel,
			adoptDetailsDescription: institutionSettings?.adoptLayDCDetailsDescription,
			adoptDetailsTitle: institutionSettings?.adoptLayDCDetailsTitle,
			adoptEmployeeSalaryDeferText: institutionSettings?.adoptLayDCEmployeeSalaryDeferText,
			adoptEmployerContributionDiscrectionLabel: institutionSettings?.adoptRSVPEmployerContributionDiscrectionLabel,
			adoptEmployerContributionScheduledLabel: institutionSettings?.adoptRSVPEmployerContributionScheduledLabel,
			adoptEmployerContributionStatus: institutionSettings?.adoptLayRSVPEmployerContributionStatus,
			adoptEmployerContributionStatusTitle: institutionSettings?.adoptEmployerContributionStatusTitle,
			adoptEmployerContributionText: institutionSettings?.adoptLayDCEmployerContributionText,
			adoptEmployerContributionPercentageLabel: institutionSettings?.adoptRSVPEmployerBaseContributionPercentageLabel,
			adoptEmployerContributioDollarsLabel: institutionSettings?.adoptRSVPEmployerMatchContributioDollarsLabel,
			adoptEmployerMatchContributionLabel: institutionSettings?.adoptLayDCEmployerMatchContributionLabel,
			adoptionInstitutionTitle: institutionSettings?.adoptLayDCadoptionInstitutionTitle,
			adoptMemberHelper: institutionSettings?.adoptLayRSVPMemberHelper,
			adoptMemberTypeBothLabel: institutionSettings?.adoptLayRSVPMemberTypeBothLabel,
			adoptMemberTypeClergyLabel: institutionSettings?.adoptLayRSVPMemberTypeClergyLabel,
			adoptMemberTypeLabel: institutionSettings?.adoptLayRSVPMemberTypeLabel,
			adoptMemberTypeLayLabel: institutionSettings?.adoptLayRSVPMemberTypeLayLabel,
			adoptMinScheduleHoursHelperText: institutionSettings?.adoptLayDCMinScheduleHoursHelperText,
			adoptMinScheduleHoursLabel: institutionSettings?.adoptLayDCMinScheduleHoursLabel,
			adoptMultipleContributionTitle: institutionSettings?.adoptMultipleContributionTitle,
			adoptMultipleContributionDescription: institutionSettings?.adoptLayDCMultipleContributionDescription,
			adoptMultipleContributionHelper: institutionSettings?.adoptLayDCMultipleContributionHelper,
			allowMultipleContributionsCheckBoxLabel: institutionSettings?.allowMultipleContributionsCheckBoxLabel,
			contributionAddEmployeeLabel: institutionSettings?.contributionAddEmployeeLabel,
			contributionAmountLabel: institutionSettings?.contributionAmountLabel,
			contributionBaseLabel: institutionSettings?.contributionBaseLabel,
			contributionBiMonthlyLabel: institutionSettings?.contributionBiMonthlyLabel,
			contributionDollarLabel: institutionSettings?.contributionDollarLabel,
			contributionEditLabel: institutionSettings?.contributionEditLabel,
			contributionRemoveLabel: institutionSettings?.contributionRemoveLabel,
			contributionMatchLabel: institutionSettings?.contributionMatchLabel,
			contributionOneTimeLabel: institutionSettings?.contributionOneTimeLabel,
			contributionOtherLabel: institutionSettings?.contributionOtherLabel,
			contributionPercentageLabel: institutionSettings?.contributionPercentageLabel,
			contributionPerPayrollLabel: institutionSettings?.contributionPerPayrollLabel,
			contributionQuarterlyLabel: institutionSettings?.contributionQuarterlyLabel,
			contributionTwiceYearlyLabel: institutionSettings?.contributionTwiceYearlyLabel,
			contributionTypeLabel: institutionSettings?.contributionTypeLabel,
			defaultBillPayorLabel: institutionSettings?.defaultBillPayorLabel,
			dioceseBillPayorLabel: institutionSettings?.dioceseBillPayorLabel,
			institutionBillPayorLabel: institutionSettings?.institutionBillPayorLabel,
			participantNameLabel: institutionSettings?.participantNameLabel,
			adoptEmployerContributionTitle: institutionSettings?.adoptEmployerContributionTitle,
			percentageFormatMessage: institutionSettings?.percentageFormatMessage,
			amountFormatMessage: institutionSettings?.amountFormatMessage,
			adoptMultipleContributionWarningMessage: institutionSettings?.adoptRSVPMultipleContributionWarningMessage,
			adoptMultipleContributionWarningTitle: institutionSettings?.adoptRSVPMultipleContributionWarningTitle,
			hasEmployerContributionStatus: false,
			isEmployerContributionRequired: false,
			hasMemberType: false,
			adoptingPlanStepperLabel: institutionSettings?.adoptLayDCPlanStepperLabel,
			docuSignSignerRoleName: institutionSettings?.docuSignSignerRoleName || 'Employer',
			docuSignSubject: institutionSettings?.adoptLayDCDocuSignSubject,
			docuSignTemplateId: '',
			docuSignFinishText: institutionSettings?.adoptLayDCDocusignFinishText,
			docuSignRejectText: institutionSettings?.docuSignRejectText,
			employerAuthorizationTitle: institutionSettings?.employerAuthorizationTitle,
			employerAuthorizationDescription: institutionSettings?.employerAuthorizationDescription,
			complyEpiscopalChurchLayPensionSystemQuestion: institutionSettings?.adoptRSVPComplyEpiscopalChurchLayPensionSystemQuestion,
			indemnifyDocuSignFinishText: institutionSettings?.adoptLayDCIndemnifyDocuSignFinishText,
			indemnifyDocuSignRejectText: institutionSettings?.docuSignRejectText,
			indemnifyDocuSignSubject: institutionSettings?.adoptLayDCIndemnifyDocuSignSubject,
			indemnifyDocuSignTemplateId: institutionSettings?.adoptLayDCIndemnifyDocuSignTemplateId,
			indemnifyWarningInstruction1: institutionSettings?.adoptLayDCIndemnifyWarningInstruction1,
			indemnifyWarningInstruction2: institutionSettings?.adoptLayDCIndemnifyWarningInstruction2,
			indemnifyWarningTitle: institutionSettings?.adoptLayDCIndemnifyWarningTitle,
			indemnifyDocuSignSignLabel: institutionSettings?.adoptLayDCIndemnifySignLabel,
			indemnifySuccessMessage: institutionSettings?.adoptLayDCIndemnifySuccessMessage,
			indemnifySkippedMessage: institutionSettings?.adoptRSVPIndemnifySkippedMessage,
			indemnifTitle: institutionSettings?.adoptLayRSVPIndemnifyTitle,
			indemnifySuccessLabel: institutionSettings?.indemnifySuccessLabel,
			indemnifySkippedLabel: institutionSettings?.indemnifySkippedLabel,
			participantsMaxMessage: institutionSettings?.participantsMaxMessage,
		};
	}

	// eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
	createSubmitModel(formValue: PlanFormModel): any {
		return null;
	}

	getNextStepUrl() {
		return this.wizardOptions?.featureRootPath;
	}

	getPreviousStepUrl() {
		if (this.isIndemnificationRequired()) {
			return `${this.wizardOptions?.featureRootPath}${PlanAdoptionRoutes.Indemnify}`;
		}

		return `${this.wizardOptions?.institutionRootPath}${ArcRoutes.PlanSummary}`;
	}

	onSuccessQueryParams() {
		return `?formType=${this.wizardOptions?.formType}`;
	}

	onSuccess(request: PlanSubmitRequest) {
		this.navigateToPath(this.getNextStepUrl(), request);
	}

	navigateToPath(nextPath: string, request: PlanSubmitRequest, clearQueryParamters: boolean = false) {
		this.history?.push({
			pathname: nextPath,
			search: (clearQueryParamters ? null : this.onSuccessQueryParams()),
			state: { request },
		});
	}

	onCancel(request: PlanCancelRequest) {
		const previousStep = this.getPreviousStepUrl();

		this.history?.push({
			pathname: previousStep,
			search: !previousStep?.includes(ArcRoutes.PlanSummary) ? `?formType=${this.wizardOptions?.formType}` : null,
			state: !previousStep?.includes(ArcRoutes.PlanSummary) ? { request } : null,
		});
	}

	onSubmit(request: PlanSubmitRequest) {
		this.onSuccess(request);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	onValidateRequest(request: PlanSubmitRequest, onValidationSucess?: (request: PlanSubmitRequest) => void, onFailure?: () => any) {
		onValidationSucess(request);
	}

	getPreviousValues(): LayDcPlanFormModel {
		return this.wizardOptions?.location?.state?.request?.values;
	}

	getDefaultFormValues(): LayDcPlanFormModel {
		const initialValues: LayDcPlanFormModel = {
			qccoQuestion1: '',
			qccoQuestion2: '',
			qccoQuestion3: '',
			minimum_scheduleds_hours: 1000,
			employer_contribution_status: EmployerContributionStatusType.Scheduled,
			employer_base_contribution_amount: 0,
			employer_base_contribution_percentage: 5,
			employer_match_contribution_amount: 0,
			employer_match_contribution_percentage: 4,
			allow_multiple_contributions: false,
			participants: [
				{
					id: 0,
				},
				{
					id: 1,
				},
				{
					id: 2,
				},
				{
					id: 3,
				},
				{
					id: 4,
				},
			],
			default_bill_payor: '',
			adoption_date: '',
			member_type: null,
			complyEpiscopalChurchLayPensionSystem: null,
			employer_base_contribution_amount_type: ContributionAmountType.Percentage,
			employer_match_contribution_amount_type: ContributionAmountType.Percentage,
			previousAdoptionDate: null,
		};

		return initialValues;
	}

	getFormInitialValue(): LayDcPlanFormModel {
		let previousValues = this.getPreviousValues();
		if (previousValues?.employer_contribution_status === EmployerContributionStatusType.Discretionary && this.calculateQccoStatus(previousValues) === QccoStatus.NotQualified) {
			previousValues = {
				...previousValues,
				employer_contribution_status: EmployerContributionStatusType.Scheduled,
			};
		}
		if (previousValues) {
			return previousValues;
		}

		const initialValues: LayDcPlanFormModel = this.getDefaultFormValues();

		return initialValues;
	}

	getFormValidationSchema(): any {
		return Yup.object().shape(createQccoValidationSchema());
	}

	calculateQccoStatus(values: LayDcPlanFormModel): QccoStatus {
		return calculateQccoStatus(values?.qccoQuestion1, values?.qccoQuestion2, values?.qccoQuestion3);
	}

	isSubmitDisabled(values: LayDcPlanFormModel): boolean {
		const qccoStatus = this.calculateQccoStatus(values);

		return qccoStatus === QccoStatus.Error;
	}

	preventButtonDisableValidation(): boolean {
		// Only when there are no values
		const previousValues = this.getPreviousValues();

		if (!previousValues) return false;

		const schema = this.getFormValidationSchema();

		const isValid = schema.isValidSync(previousValues);

		return !!previousValues && isValid;
	}

	getAdoptionDateOptions(): { text: string, value: string }[] {
		const currentDate = new Date();
		const options: { text: string, value: string }[] = [];

		// Add the current next 3 day of the month to the list.
		const currentDay = currentDate.getDate();
		const dateDelta = (currentDay === 1) ? 0 : 1;

		for (let i = 0; i < 3; i += 1) {
			const periodDate = new Date();
			periodDate.setMonth(currentDate.getMonth() + dateDelta + i);
			periodDate.setDate(1);
			options.push({
				text: formatDateMonthNumber(periodDate),
				value: formatDate(periodDate, false),
			});
		}

		return options;
	}

	getContributionTypeOptions(employerContributionStatus: EmployerContributionStatusType): { text: string, value: string }[] {

		const content = this.getFormContentModel();

		const dollarOptions = [
			{ text: content.contributionDollarLabel, value: ContributionType.None },
			{ text: content.contributionPerPayrollLabel, value: ContributionType.PerPayrollPeriod },
			{ text: content.contributionBiMonthlyLabel, value: ContributionType.BiMonthly },
			{ text: content.contributionQuarterlyLabel, value: ContributionType.Quarterly },
			{ text: content.contributionTwiceYearlyLabel, value: ContributionType.TwiceYearly },
			{ text: content.contributionOneTimeLabel, value: ContributionType.OneTimePayment },
		];

		const percentageOptions = [
			{ text: content.contributionPercentageLabel, value: ContributionType.None },
			{ text: content.contributionBaseLabel, value: ContributionType.Base },
			{ text: content.contributionMatchLabel, value: ContributionType.Match },
		];

		const otherOptions = [
			{ text: content.contributionOtherLabel, value: ContributionType.Other },
		];

		if (employerContributionStatus === EmployerContributionStatusType.Discretionary) {
			return [
				...dollarOptions,
				...otherOptions,
			];
		}

		return [
			...percentageOptions,
			...dollarOptions,
			...otherOptions,
		];
	}

	getMemberTypeOptions(): { label: string, value: string }[] {

		const content = this.getFormContentModel();

		const options = [
			{ label: content.adoptMemberTypeClergyLabel, value: AdoptionMemberType.Clergy },
			{ label: content.adoptMemberTypeLayLabel, value: AdoptionMemberType.Lay },
			{ label: content.adoptMemberTypeBothLabel, value: AdoptionMemberType.Both },
		];

		return options;
	}

	getEmployerContributionStatusOptions(): { label: string, value: string }[] {

		const content = this.getFormContentModel();

		const options = this.calculateQccoStatus(this.getFormInitialValue()) === QccoStatus.Qualified ? [
			{ label: content.adoptEmployerContributionScheduledLabel, value: EmployerContributionStatusType.Scheduled },
			{ label: content.adoptEmployerContributionDiscrectionLabel, value: EmployerContributionStatusType.Discretionary },
		] : [
			{ label: content.adoptEmployerContributionScheduledLabel, value: EmployerContributionStatusType.Scheduled },
		];

		return options;
	}

	getContributionAmountTypeOptions(): { label: string, value: string }[] {

		const content = this.getFormContentModel();

		const options = [
			{ label: content.adoptEmployerContributioDollarsLabel, value: ContributionAmountType.Dollar },
			{ label: content.adoptEmployerContributionPercentageLabel, value: ContributionAmountType.Percentage },
		];

		return options;
	}

	getMappedContributionAmountTypeForContributionType(contributionType: string): ContributionAmountType {
		switch (contributionType) {
			case ContributionType.Base:
			case ContributionType.Match:
				return ContributionAmountType.Percentage;

			case ContributionType.OneTimePayment:
			case ContributionType.PerPayrollPeriod:
			case ContributionType.BiMonthly:
			case ContributionType.Quarterly:
			case ContributionType.TwiceYearly:
			case ContributionType.Other:
				return ContributionAmountType.Dollar;

			default:
			case ContributionType.None:
				return ContributionAmountType.None;
		}
	}

	mapParticipantToContributionOverride(participant: ParticipantModel): IContributionOverride {
		if (!participant) return null;

		const { contribution_value_type } = participant;

		const contributionOverride: IContributionOverride = {
			employee_name: participant.participant_name,
			contribution_type: participant.contribution_type,
		};

		if (contribution_value_type === ContributionAmountType.Dollar) {
			contributionOverride.contribution_amount = getCurrencyFormat(participant?.contribution_amount);
		}

		if (contribution_value_type === ContributionAmountType.Percentage) {
			contributionOverride.contribution_amount = `${participant.contribution_percentage?.toFixed(0)}%`;
		}

		if (contribution_value_type === ContributionAmountType.Other) {
			contributionOverride.contribution_amount = getCurrencyFormat(participant?.contribution_amount);
		}

		return contributionOverride;
	}

	getFormType(): FormTypes {
		return FormTypes.ADOPT_INSTITUTION_PLAN_LAY_DC;
	}

	isIndemnificationStepperStepVisible(): boolean {

		const initialValues = this.getFormInitialValue();
		const isIndemnificationRequired = this.isIndemnificationRequired();
		const indemnificationRejected = initialValues.complyEpiscopalChurchLayPensionSystem === YesNoIndicators.No;

		return isIndemnificationRequired && !indemnificationRejected;
	}

	getStepperSteps(): string[] {

		const indemnificationStepperStepVisible = this.isIndemnificationStepperStepVisible();

		if (indemnificationStepperStepVisible) {
			return [AdoptionStep.INDEMNIFY, AdoptionStep.QUESTIONS, AdoptionStep.DETAILS, AdoptionStep.REVIEW];
		}

		return [AdoptionStep.QUESTIONS, AdoptionStep.DETAILS, AdoptionStep.REVIEW];
	}

	getStepperProgressItems(): ProgressItem[] {

		const institutionSettings = this.epiContent?.institutionSettingsState?.epiContent;

		const indemnificationStepperStepVisible = this.isIndemnificationStepperStepVisible();

		const indemnificationStep = indemnificationStepperStepVisible ? [{
			stepName: institutionSettings?.indemnifyStepTitle,
			linkUrl: PlanAdoptionRoutes.Indemnify,
			step: AdoptionStep.INDEMNIFY,
		}] : [];

		return [
			...indemnificationStep,
			{
				stepName: institutionSettings?.qccoStepTitle,
				linkUrl: PlanAdoptionRoutes.QccoQuestions,
				step: AdoptionStep.QUESTIONS,
			},
			{
				stepName: institutionSettings?.planDetailStepTitle,
				linkUrl: PlanAdoptionRoutes.AdoptionDetails,
				step: AdoptionStep.DETAILS,
			},
			{
				stepName: institutionSettings?.reviewStepTitle,
				linkUrl: PlanAdoptionRoutes.Review,
				step: AdoptionStep.REVIEW,
			},
		];
	}

	prepareDocuSignRequest(): DocuSignRequest {

		const content = this.getFormContentModel();

		const docuSignRequest: DocuSignRequest = {
			loginTime: this.wizardOptions.loginTime,
			authToken: this.wizardOptions.authToken,
			subject: content?.docuSignSubject,
			templateId: content?.docuSignTemplateId,
			signerEmail: this.wizardOptions?.person?.personal_email_address,
			signerName: `${this.wizardOptions?.person?.first_name}${this.wizardOptions?.person?.middle_name ? ' ' : ` ${this.wizardOptions?.person?.middle_name} `}${this.wizardOptions?.person?.last_name}`,
			signerClientId: this.wizardOptions?.person?.personal_email_address || this.wizardOptions?.person?.person_guid,
			signerRoleName: content?.docuSignSignerRoleName || 'Employer',
			customFieldTemplateId: content?.docuSignTemplateId,
			customFieldClientId: this.wizardOptions?.currentInstitution?.org_client_nbr,
		};

		return docuSignRequest;
	}

	prepareIndemnificationDocuSignRequest(): DocuSignRequest {

		const content = this.getFormContentModel();

		const docuSignRequest: DocuSignRequest = {
			loginTime: this.wizardOptions.loginTime,
			authToken: this.wizardOptions.authToken,
			subject: content?.indemnifyDocuSignSubject,
			templateId: content?.indemnifyDocuSignTemplateId,
			signerEmail: this.wizardOptions?.person?.personal_email_address,
			signerName: `${this.wizardOptions?.person?.first_name}${this.wizardOptions?.person?.middle_name ? ' ' : ` ${this.wizardOptions?.person?.middle_name} `}${this.wizardOptions?.person?.last_name}`,
			signerClientId: this.wizardOptions?.person?.personal_email_address || this.wizardOptions?.person?.person_guid,
			signerRoleName: content?.docuSignSignerRoleName || 'Employer',
			customFieldTemplateId: content?.indemnifyDocuSignTemplateId,
			customFieldClientId: this.wizardOptions?.currentInstitution?.org_client_nbr,
		};

		return docuSignRequest;
	}

	isIndemnificationRequired(): boolean {
		return this.wizardOptions?.planSummary?.lay_db?.active?.length > 0;
	}

	isComplyEpiscopalChurchLayPensionSystemQuestionRequired(): boolean {
		return false;
	}

	isIndemnificationComplete(): boolean {
		const initialValues = this.getDefaultFormValues();
		const isIndemnificationRequired = this.isIndemnificationRequired();

		return !isIndemnificationRequired ||
			(isIndemnificationRequired && (!initialValues.indemnificationEnvelopeId || !initialValues?.complyEpiscopalChurchLayPensionSystem));
	}
}